import { useMe } from '@/api/auth/auth.hooks';
import { InfoContainer } from '@/components/base/info-container.component';
import { theme } from '@/config/theme';
import { useSignOut } from '@hooks/use-sign-out';
import {
  Lock as LockIcon,
  Logout as LogoutIcon,
  PeopleAlt as PeopleAltIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LinkButton } from '../components/auth-link-button.component';
import { useTranslation } from 'react-i18next';
import eLinkLogoWhite from '@assets/elink_distribution_ag_logo_white.svg';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

export function AuthPartnerSelectPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const signOut = useSignOut();
  const isFreshlySignedUp = searchParams.get('success') != null;
  const { data: me, isPending } = useMe();
  const { contactEmployees: _contactEmployees } = me || {};
  const contactEmployees = _contactEmployees || [];

  const handleSignOut = () => {
    signOut();
    navigate('/auth');
  };

  // Confusing legacy name kept to minimise the page-template diff: this is
  // really "is this user internal staff?", which is what gates the
  // "Interner Bereich" / impersonation links.
  const isAdmin = me?.accountType === 'INTERNAL';

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          background:
            'linear-gradient(135deg, #02344C 0%, #063f5c 50%, #04516F 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          px: 2,
          pt: { xs: 10, sm: 14 },
          pb: 6,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Grid pattern overlay with radial fade */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Cpath d='M0 0h40v40H0z' fill='none' stroke='rgba(255,255,255,0.07)' stroke-width='0.5'/%3E%3C/svg%3E")`,
            maskImage:
              'radial-gradient(ellipse 60% 50% at 50% 40%, black 0%, transparent 85%)',
            WebkitMaskImage:
              'radial-gradient(ellipse 60% 50% at 50% 40%, black 0%, transparent 85%)',
            pointerEvents: 'none',
          }}
        />

        {/* Content */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: 460,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* Logo — entrance delay 0ms */}
          <Box
            sx={{
              mb: 5,
              opacity: 0,
              animation: `${fadeIn} 0.5s ease-out 0ms forwards`,
            }}
          >
            <img
              src={eLinkLogoWhite}
              alt="eLink Distribution AG"
              style={{ height: 36 }}
            />
          </Box>

          {/* Header — entrance delay 80ms */}
          <Box
            sx={{
              textAlign: 'center',
              mb: 3.5,
              opacity: 0,
              animation: `${fadeIn} 0.5s ease-out 80ms forwards`,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: 'white',
                fontWeight: 700,
                mb: 0.75,
                letterSpacing: '-0.01em',
              }}
            >
              {t('auth.accountSelection.title')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.5)',
                maxWidth: 340,
                mx: 'auto',
                lineHeight: 1.6,
                letterSpacing: '0.01em',
              }}
            >
              {t('auth.accountSelection.description')}
            </Typography>
          </Box>

          {/* Success alert — entrance delay 160ms */}
          {isFreshlySignedUp && (
            <Box
              sx={{
                width: '100%',
                mb: 2,
                opacity: 0,
                animation: `${fadeIn} 0.5s ease-out 160ms forwards`,
              }}
            >
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                {t('auth.accountSelection.successMessage')}
              </Alert>
            </Box>
          )}

          {/* Partner list — cards stagger from 300ms via LinkButton index */}
          {isPending ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress sx={{ color: 'rgba(255,255,255,0.4)' }} />
            </Box>
          ) : contactEmployees.length > 0 ? (
            <Stack spacing={1.5} sx={{ width: '100%' }}>
              {contactEmployees.map((employee, index) => (
                <LinkButton
                  key={employee.id}
                  employee={employee}
                  index={index}
                />
              ))}
            </Stack>
          ) : (
            <Box
              sx={{
                width: '100%',
                bgcolor: 'white',
                borderRadius: 3,
                p: 3,
                opacity: 0,
                animation: `${fadeIn} 0.5s ease-out 300ms forwards`,
              }}
            >
              <InfoContainer
                title={t('auth.accountSelection.noAccount.title')}
                description={t('auth.accountSelection.noAccount.description')}
              />
            </Box>
          )}

          {/* Footer — fades in last */}
          <Box
            sx={{
              width: '100%',
              mt: 4,
              opacity: 0,
              animation: `${fadeIn} 0.5s ease-out 500ms forwards`,
            }}
          >
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ justifyContent: 'center', flexWrap: 'wrap', gap: 0.5 }}
            >
              {isAdmin && (
                <>
                  <Button
                    startIcon={<LockIcon sx={{ fontSize: '16px !important' }} />}
                    size="small"
                    onClick={() => navigate('/i')}
                    sx={{
                      color: 'rgba(255,255,255,0.45)',
                      textTransform: 'none',
                      fontSize: '0.76rem',
                      '&:hover': { color: 'rgba(255,255,255,0.85)' },
                    }}
                  >
                    {t('auth.accountSelection.internalArea')}
                  </Button>
                  <Button
                    startIcon={<PeopleAltIcon sx={{ fontSize: '16px !important' }} />}
                    size="small"
                    onClick={() => navigate('/impersonate')}
                    sx={{
                      color: 'rgba(255,255,255,0.45)',
                      textTransform: 'none',
                      fontSize: '0.76rem',
                      '&:hover': { color: 'rgba(255,255,255,0.85)' },
                    }}
                  >
                    {t('auth.accountSelection.impersonate')}
                  </Button>
                </>
              )}
              <Button
                startIcon={<LogoutIcon sx={{ fontSize: '16px !important' }} />}
                size="small"
                onClick={handleSignOut}
                sx={{
                  color: 'rgba(255,255,255,0.45)',
                  textTransform: 'none',
                  fontSize: '0.76rem',
                  '&:hover': { color: 'rgba(255,255,255,0.85)' },
                }}
              >
                {t('userMenu.logout')}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
